/**
 * Keyboard Tracking (Key Follow) Utility
 *
 * Adjusts filter cutoff frequency based on the note being played.
 * This helps reduce buzzing at lower pitches by rolling off high frequencies,
 * while allowing higher pitches to remain brighter.
 *
 * Common in classic synths like Moog, Prophet, etc.
 */

import * as Tone from "tone";
import type { SynthFilter } from "./ccMapping";

// Reference pitch for keyboard tracking (C3 = middle of typical bass range)
const REFERENCE_NOTE = "C3";
const REFERENCE_FREQUENCY = 130.81; // Hz

/**
 * Calculate filter cutoff adjustment based on note pitch
 *
 * @param note - The note being played (e.g., "D#2", "A#2")
 * @param baseCutoff - The base filter cutoff frequency in Hz
 * @param trackingAmount - How much the filter follows pitch (0 = none, 1 = full, 0.5 = typical)
 * @returns The adjusted filter cutoff frequency
 */
export function calculateKeyboardTracking(
  note: string,
  baseCutoff: number,
  trackingAmount: number = 0.5
): number {
  try {
    // Convert note to frequency
    const noteFrequency = Tone.Frequency(note).toFrequency();

    // Calculate the ratio from reference pitch
    const ratio = noteFrequency / REFERENCE_FREQUENCY;

    // Apply tracking: filter follows pitch by trackingAmount
    // ratio^trackingAmount gives us partial following
    // At trackingAmount=0.5, an octave up doubles the cutoff by sqrt(2) ≈ 1.41
    const adjustmentFactor = Math.pow(ratio, trackingAmount);

    // Calculate new cutoff
    const adjustedCutoff = baseCutoff * adjustmentFactor;

    // Clamp to reasonable range (20Hz - 20kHz)
    return Math.max(20, Math.min(20000, adjustedCutoff));
  } catch (error) {
    console.warn(`[KEYBOARD TRACKING] Failed to calculate for note ${note}:`, error);
    return baseCutoff;
  }
}

/**
 * Apply keyboard tracking to a filter
 *
 * @param filter - The Tone.js filter or FilterBridge to adjust
 * @param note - The note being played
 * @param baseCutoff - The base filter cutoff (stored CC value)
 * @param trackingAmount - How much to track (0-1, default 0.5)
 */
export function applyKeyboardTracking(
  filter: SynthFilter,
  note: string,
  baseCutoff: number,
  trackingAmount: number = 0.5
): void {
  const adjustedCutoff = calculateKeyboardTracking(note, baseCutoff, trackingAmount);

  // Apply to filter
  if ('setCutoff' in filter) {
    // WASM FilterBridge
    (filter as { setCutoff: (freq: number) => void }).setCutoff(adjustedCutoff);
  } else {
    // Tone.js Filter
    filter.frequency.value = adjustedCutoff;
  }

  console.log(
    `🎹 [KEY TRACKING] Note: ${note}, Base: ${baseCutoff.toFixed(0)}Hz → Adjusted: ${adjustedCutoff.toFixed(0)}Hz (tracking: ${trackingAmount})`
  );
}

/**
 * Get frequency for a note (utility for debugging)
 */
export function getNoteFrequency(note: string): number {
  return Tone.Frequency(note).toFrequency();
}
